import React, { useState } from "react";
import {
  showError,
  showSuccess,
} from "../../../../../../../../utils/showMessage.js";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Images from "../../../../../../../../utils/images.js";
import CrossButton from "../../../../../../../common/ButtonSpinner/CrossButton.js";
import GeneralButton from "../../../../../../../common/SaveButton/GeneralButton.js";
import CustomModal from "../../../../../../../common/CustomModal/CustomModal.js";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { Tooltip } from "@mui/material";
import { FiPlusCircle } from "react-icons/fi";
import { useReactSelectTheme } from "../../../../../../../../utils/useReactSelectTheme.js";

const DATATYPE_OPTIONS = [
  { value: "INT", label: "INT" },
  { value: "VARCHAR", label: "VARCHAR" },
  { value: "FLOAT", label: "FLOAT" },
  { value: "BOOLEAN", label: "BOOLEAN" },
  { value: "CHAR", label: "CHAR" },
  { value: "DECIMAL", label: "DECIMAL" },
];

const ColumnForm = ({ column, index, handleColumnChange, selectStyles, selectTheme }) => (
  <div className="mb-4 p-3 border rounded">
    <Row className="g-3">
      {/* Column Name */}
      <Col md={["VARCHAR", "CHAR"].includes(column.type) ? 4 : 6}>
        <Form.Label className="input_label d-flex align-items-center">
          Column Name
          <span className="text-danger ms-1">*</span>
          <Tooltip
            title="The column name should only contain alphanumeric characters and underscores (_). Special characters are not allowed."
            placement="top-end"
          >
            <img
              src={Images.InfoIcon}
              alt="info"
              width="15"
              height="15"
              className="cursor_pointer ms-2"
            />
          </Tooltip>
        </Form.Label>
        <Form.Control
          type="text"
          required
          value={column.name}
          placeholder="Enter column name"
          className="form_input_field mt-2"
          onChange={(e) => handleColumnChange(index, "name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">
          Column name cannot be empty
        </Form.Control.Feedback>
      </Col>

      {/* Datatype */}
      <Col md={["VARCHAR", "CHAR"].includes(column.type) ? 4 : 6}>
        <Form.Label className="input_label d-flex align-items-center">
          Data Type
          <span className="text-danger ms-1">*</span>
          <Tooltip
            title="Select the appropriate data type for your column. VARCHAR and CHAR require length specification."
            placement="top-end"
          >
            <img
              src={Images.InfoIcon}
              alt="info"
              width="15"
              height="15"
              className="cursor_pointer ms-2"
            />
          </Tooltip>
        </Form.Label>
        <Select
          value={DATATYPE_OPTIONS.find((option) => option.value === column.type)}
          onChange={(selectedOption) => {
            if (selectedOption) {
              handleColumnChange(index, "type", selectedOption.value);
            }
          }}
          options={DATATYPE_OPTIONS}
          styles={selectStyles}
          theme={selectTheme}
          className="mt-2"
        />
      </Col>

      {["VARCHAR", "CHAR"].includes(column.type) && (
        <Col md={4}>
          <Form.Label className="input_label d-flex align-items-center">
            Length
            <span className="text-danger ms-1">*</span>
          </Form.Label>
          <Form.Control
            type="number"
            value={column.length || ""}
            placeholder={`Enter length for ${column.type}`}
            className="form_input_field mt-2"
            onChange={(e) => handleColumnChange(index, "length", e.target.value)}
          />
        </Col>
      )}
    </Row>
  </div>
);

const AddColumnPopup = ({
  visible,
  onClose,
  onSave,
  tableName,
  resourceId,
  systemUserId,
  appType,
  databaseName,
  loading,
  setLoading,
  setAddColumn,
  databaseInfo,
}) => {
  const [columns, setColumns] = useState([{ name: "", type: "", length: "" }]);

  const handleColumnChange = (index, field, value) => {
    const updatedColumns = [...columns];
    updatedColumns[index] = { ...updatedColumns[index], [field]: value };
    setColumns(updatedColumns);
  };

  const handleSave = () => {
    if (columns.every((col) => col.name && col.type)) {
      onSave(resourceId, systemUserId, appType, databaseName, tableName, columns);
      setColumns([{ name: "", type: "", length: "" }]);
    }
  };

  const addNewColumn = () => {
    setColumns([...columns, { name: "", type: "", length: "" }]);
  };

  const removeColumn = (index) => {
    if (columns.length > 1) {
      const updatedColumns = columns.filter((_, i) => i !== index);
      setColumns(updatedColumns);
    }
  };

  const { styles: selectStyles, theme: selectTheme } = useReactSelectTheme();


  return (
    <CustomModal 
      open={visible} 
      handleClose={onClose}
      width="40%"
    >
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="d-flex align-items-center mb-1">
              {databaseInfo === "MySQL" && (
                <img src={Images.mysql} alt="MySQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "PostgreSQL" && (
                <img src={Images.pssql} alt="PostgreSQL" width="24" height="24" className="me-2" />
              )}
              {databaseInfo === "MSSQL" && (
                <img src={Images.mssql} alt="MSSQL" width="24" height="24" className="me-2" />
              )}
              <h3 className="border-0 customize-column-name mb-0">
                Add Columns
              </h3>
            </div>
            <p className="text-muted mb-0 fs-14px">
              Add new columns to the <code className="bg-light px-2 py-1 rounded">{tableName}</code> table
            </p>
          </div>
          <CrossButton onClick={onClose} />
        </div>

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="mb-4">
            {columns.map((column, index) => (
              <div key={index} className="position-relative">
                <ColumnForm
                  column={column}
                  index={index}
                  handleColumnChange={handleColumnChange}
                  selectStyles={selectStyles}
                  selectTheme={selectTheme}
                />
                {columns.length > 1 && (
                  <button
                    type="button"
                    className="text-muted position-absolute"
                    style={{ top: "8px", right: "8px", fontSize: "18px", lineHeight: "1", background: "none", border: "none" }}
                    onClick={() => removeColumn(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            
            <div className="d-flex justify-content-start mt-3">
              <button
                type="button"
                onClick={addNewColumn}
                className="btn btn-link d-flex justify-content-start align-items-center"
              >
                <FiPlusCircle size={16} className="me-2" />
                Add Column
              </button>
            </div>
          </div>

          <Row className="pt-2 mb-3 justify-content-end">
            <Col sm="auto">
              <GeneralButton
                onClickEvent={onClose}
                className="me-1"
                value="Cancel"
                color="#505050"
                variant="outlined"
                size="large"
              />
            </Col>
            <Col sm="auto" className="me-sm-2 p-0">
              {loading ? (
                <GeneralButton
                  variant="contained"
                  disabled={true}
                  className="me-1"
                  value={
                    <img src={Images.Loader} width="26px" height="26px" />
                  }
                  size="large"
                />
              ) : (
                <GeneralButton
                  className="me-1"
                  value="Add"
                  variant="contained"
                  size="large"
                />
              )}
            </Col>
          </Row>
        </Form>
      </div>
    </CustomModal>
  );
};

export default AddColumnPopup;
